/**
 * Ghanaian traditional marriage essay — images: public/images/w1.webp … w7.jpeg
 * Headline on page: main title + covenant subtitle below.
 * Strong alternate titles (for promos / social): "Not Just a Wedding: What Ghanaian Marriage Customs Teach the World";
 * "Inside the Ghanaian Marriage Ceremony: Family, Honor, and Cultural Meaning.";
 * "Why Ghanaian Traditional Marriage Is About More Than Bride Price."
 */

const base = process.env.PUBLIC_URL || '';

export const traditionalMarriageMeta = {
  id: 'ghanaian-traditional-marriage',
  title: 'The Ghanaian Traditional Marriage Ceremony',
  subtitle: 'More Than a Wedding: A Living Covenant Between Families, History, and Community',
};

export const traditionalMarriageBlocks = [
  {
    type: 'p',
    text:
      'In Ghana, a traditional marriage ceremony is not simply an event. It is not merely a prelude to a white wedding, nor is it a quaint cultural add-on performed for photographs and applause. It is, in its deepest meaning, a public act of honor, negotiation, belonging, continuity, and communal blessing. It is one of the great social non-negotiables of Ghanaian life: a ceremony where two people may be the center of attention, but two families, two lineages, and often two entire communities are the true participants.',
  },
  {
    type: 'p',
    text:
      'To understand a Ghanaian traditional marriage is to understand something profound about Ghana itself. Here, marriage is not treated as a private romance sealed off from society. Love matters, yes. Attraction matters. Personal choice matters. But marriage is also understood as social architecture. It is a bridge between houses. It is a covenant witnessed not only by parents, but by siblings, aunts, uncles, grandparents, elders, clergy, family friends, and community leadership. In many homes, it is one of the few occasions where every layer of human belonging stands together in one place: blood relatives, in-laws, neighbors, spiritual leaders, and old family friends who have earned the right to be called family too.',
  },
  {
    type: 'p',
    text:
      'A Ghanaian traditional marriage ceremony is often colorful, elegant, verbal, symbolic, and deeply theatrical in the best sense of the word. It unfolds with dignity, humor, proverbs, ritual courtesy, and social intelligence. Nothing important is rushed. Every gesture means something. Every item presented carries a message. Every elder present is more than a guest; they are a witness, a guardian of custom, and a living archive of what marriage is supposed to mean.',
  },
  {
    type: 'figure',
    src: `${base}/images/w1.webp`,
    alt: 'A Ghanaian traditional wedding. Bridesmaid dancing.',
    caption: 'A Ghanaian traditional wedding. Bridesmaid Dancing.',
  },
  {
    type: 'h2',
    text: 'What the Bride Price Really Means',
  },
  {
    type: 'p',
    text:
      'At the center of the ceremony is a process sometimes misunderstood by outsiders: the presentation of the bride price or dowry items by the groom’s family to the bride’s family. To foreign ears, especially in places where African customs are too often flattened by stereotype, this may sound transactional. It is not. It is not the purchase of a woman. It is not a market exchange. It is, rather, a structured cultural language of respect.',
  },
  {
    type: 'p',
    text:
      'The groom’s family comes formally to ask for the woman’s hand in marriage. They do not arrive empty-handed. They come bearing drinks, gifts, and the agreed marriage items requested by the bride’s family according to custom. These may include cloth, jewelry, money, drinks, cola nuts, Bible or prayer items depending on the faith tradition of the family, and other symbolic offerings. The presentation acknowledges that the bride did not emerge from nowhere. She was raised, protected, guided, and loved by a network of people. Her parents poured into her. Her siblings grew with her. Her grandparents prayed over her. Her uncles and aunts helped shape her. The marriage ceremony publicly recognizes that her life has been held by many hands.',
  },
  {
    type: 'figure',
    src: `${base}/images/w2.webp`,
    alt: 'A Ghanaian traditional wedding. Entertainment performers.',
    caption: 'A Ghanaian traditional wedding. Entertainment Performers.',
  },
  {
    type: 'p',
    text:
      'In many Ghanaian traditions, these items are not solely for the parents. The broader family—siblings, maternal uncles, paternal relatives, grandparents, and other key kin—may be recognized because marriage is understood as a union with a whole relational world, not just with an individual. That communal structure matters deeply. It tells the groom’s family: we see where this woman comes from, and we honor the people who made her who she is.',
  },
  {
    type: 'p',
    text:
      'There is something powerful in that acknowledgment. In many modern societies, marriage has become so individualized that family is sometimes reduced to a seating chart and a photo opportunity. In Ghana, traditional marriage insists on something older and wiser: no one arrives at adulthood alone. To marry a person is also to honor the roots that nourished them.',
  },
  {
    type: 'figure',
    src: `${base}/images/w3.webp`,
    alt: 'A Ghanaian traditional wedding. Ceremony and family.',
    caption: 'A Ghanaian traditional wedding. Ceremony and family.',
  },
  {
    type: 'h2',
    text: 'Honoring the Bride',
  },
  {
    type: 'p',
    text:
      'Then there is the bride herself, radiant not only in beauty, but in significance. She is presented with gifts, often carefully chosen, elegant, practical, and symbolic. Gold jewelry may be offered. Fine cloth may be laid before her. Sandals, bags, and family heirlooms may appear. But among the most culturally meaningful gestures is the giving of seed money—capital intended to help the woman begin a trade, support a business idea, or strengthen her economic footing within married life.',
  },
  {
    type: 'figure',
    src: `${base}/images/w4.webp`,
    alt: 'A Ghanaian traditional wedding. Gifts and celebration.',
    caption: 'A Ghanaian traditional wedding. Gifts and celebration.',
  },
  {
    type: 'p',
    text:
      'This detail is especially important, and it deserves far more attention than it usually receives in simplified discussions of African marriage customs. The seed money reflects a deeply rooted Ghanaian understanding that a wife is not entering marriage merely as a dependent ornament of domestic life. She is expected to be a capable, respected, economically aware partner. In many Ghanaian communities, women have long been traders, entrepreneurs, market leaders, financial organizers, and stabilizing forces of the household economy. The symbolic or practical provision of startup money recognizes her agency. It says, in effect: you are entering this union not only to build a home emotionally, but to help build it economically, wisely, and with dignity.',
  },
  {
    type: 'p',
    text:
      'This is not a small thing. It is a window into a broader philosophy of partnership. The woman is honored, adorned, and celebrated, yes—but also positioned as a contributor, a builder, and a person of practical value in the shared life ahead. For a non-Ghanaian observer, this can be one of the most eye-opening elements of the ceremony. Beneath the beauty and pageantry lies a serious social principle: marriage should be affectionate, but it should also be enabling.',
  },
  {
    type: 'figure',
    src: `${base}/images/w5.webp`,
    alt: 'A Ghanaian traditional wedding. Bride and groom.',
    caption: 'A Ghanaian traditional wedding. Bride & Groom.',
  },
  {
    type: 'h2',
    text: 'Hospitality, Faith, and Words That Bless',
  },
  {
    type: 'p',
    text:
      'Hospitality, too, is central to the ceremony. Once the guests and visiting family arrive, the bride’s family welcomes them with food and drink. This is not incidental catering. It is cultural diplomacy through generosity. A well-received guest becomes part of the day’s peace. Plates are served, drinks are offered, and the atmosphere becomes one of warmth, abundance, and public goodwill. The meal says what words alone cannot: you are received here; let this union begin in welcome, not tension.',
  },
  {
    type: 'figure',
    src: `${base}/images/w6.webp`,
    alt: 'A Ghanaian traditional wedding. Reception.',
    caption: 'A Ghanaian traditional wedding. Reception.',
  },
  {
    type: 'p',
    text:
      'And the atmosphere—oh, the atmosphere—is unlike anything one forgets easily. There is laughter, but also solemnity. There is style, but also structure. There are beautifully coordinated family cloths, the rustle of kente and lace, the graceful authority of mothers, the composed scrutiny of fathers, the knowing smiles of aunties, the strategic dignity of uncles, the watchful pride of grandparents, and the emotional electricity of friends who know they are witnessing not just romance, but heritage in motion.',
  },
  {
    type: 'p',
    text:
      'Often, clergy are present too, especially in families where Christian or Muslim faith practices intertwine with customary rites. Their role reflects another important truth about Ghanaian social life: tradition and faith are often not enemies, but companions. Community leadership may also be represented formally or informally—chiefly families, respected elders, opinion leaders, and long-standing family friends. Their presence enlarges the ceremony’s meaning. Marriage is not just a legal or emotional agreement; it is a social declaration that this union has been seen, endorsed, and blessed by the moral world around it.',
  },
  {
    type: 'p',
    text:
      'The language used during the ceremony is often one of the most beautiful parts. In many traditions, spokespersons speak on behalf of each family. They do not blurt or rush. They present, ask, respond, and clarify with care, wit, and ceremonial intelligence. Proverbs may be used. Metaphors may soften serious moments. Humor often relieves tension. A woman may be “asked for” in language full of courtesy. The groom’s family may be tested gently, even playfully, to prove sincerity. Outsiders watching closely soon realize that this is not awkward ritualism. It is social art.',
  },
  {
    type: 'p',
    text:
      'The traditional marriage ceremony also performs another vital cultural task: it makes marriage legible to the community. It tells everyone involved that this union is not hidden, casual, or undefined. It has witnesses. It has blessings. It has obligations. It has memory. In an era of fragile relationships and increasingly private commitments, there is something almost radical about this public seriousness. Ghanaian custom says marriage should not drift into existence vaguely. It should be named, witnessed, and held.',
  },
  {
    type: 'figure',
    src: `${base}/images/w7.webp`,
    alt: 'A Ghanaian traditional wedding. Bride and groom on the dance floor.',
    caption: 'A Ghanaian traditional wedding. Bride & Groom. On The Dance Floor.',
  },
  {
    type: 'p',
    text: 'And that may be the deepest beauty of all.',
  },
  {
    type: 'p',
    text:
      'A Ghanaian traditional marriage ceremony is, at heart, a statement that love does not have to stand alone to be real. It can stand inside family. It can stand inside responsibility. It can stand inside custom. It can be adorned with music, wrapped in cloth, fed with generosity, examined by elders, blessed by clergy, and strengthened by communal memory. It can be tender without being flimsy. It can be beautiful without being shallow. It can be romantic and still profoundly social.',
  },
  {
    type: 'p',
    text:
      'For the non-Ghanaian reader trying to understand this ceremony, perhaps the clearest way to say it is this: a Ghanaian traditional marriage is where affection meets ancestry, where partnership meets public honor, and where two people step forward only because many others stand behind them.',
  },
  {
    type: 'p',
    text:
      'It is not just a wedding.\n\nIt is a family covenant.\n\nIt is not just a day of gifts.\n\nIt is a language of respect.\n\nIt is not just ceremony.\n\nIt is society teaching itself, once again, how to bless a union properly.',
  },
  {
    type: 'p',
    text:
      'And in that blessing—in the cloth, the gifts, the food, the seed money, the laughter, the elders, the prayers, and the formal asking—Ghana reveals one of its most beautiful truths: a strong marriage is never built by two hearts alone. It is built by memory, witness, generosity, and the gathered strength of a people.',
  },
];
